import { createTheme, alpha } from '@mui/material';

// Modern color palette
const primaryMain = '#9333ea'; // Primary purple
const secondaryMain = '#7c3aed'; // Secondary purple
const successMain = '#059669'; // A fresh green
const warningMain = '#d97706'; // A warm amber
const errorMain = '#dc2626'; // A bright red
const infoMain = '#0891b2'; // A cyan blue

// Font configuration
declare module '@mui/material/styles' {
  interface TypographyVariants {
    hero: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    hero?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    hero: true;
  }
}

export const theme = createTheme({
  // Modern typography using Inter font
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    h1: {
      fontSize: '3.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2.75rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '2.25rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    hero: {
      fontSize: '4rem',
      fontWeight: 800,
      letterSpacing: '-0.03em',
      lineHeight: 1.1,
    },
    subtitle1: {
      fontSize: '1.125rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none', // More modern approach
      fontWeight: 600,
    },
  },

  // Modern color palette
  palette: {
    mode: 'light',
    primary: {
      main: primaryMain,
      light: '#a855f7', // Lighter purple
      dark: '#7e22ce', // Darker purple
      contrastText: '#fff',
    },
    secondary: {
      main: secondaryMain,
      light: '#8b5cf6', // Lighter secondary purple
      dark: '#6d28d9', // Darker secondary purple
      contrastText: '#fff',
    },
    success: {
      main: successMain,
      light: alpha(successMain, 0.8),
      dark: alpha(successMain, 1.0),
    },
    warning: {
      main: warningMain,
      light: alpha(warningMain, 0.8),
      dark: alpha(warningMain, 1.0),
    },
    error: {
      main: errorMain,
      light: alpha(errorMain, 0.8),
      dark: alpha(errorMain, 1.0),
    },
    info: {
      main: infoMain,
      light: alpha(infoMain, 0.8),
      dark: alpha(infoMain, 1.0),
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },

  // Modern shape and spacing
  shape: {
    borderRadius: 12,
  },

  // Component overrides for a modern look
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '8px 20px',
          fontSize: '0.9375rem',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            },
            '&.Mui-focused': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha('#ffffff', 0.8),
        },
      },
    },
  },
});

// Create a dark mode version
export const darkTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    mode: 'dark',
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1',
    },
  },
  components: {
    ...theme.components,
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha('#0f172a', 0.8),
        },
      },
    },
  },
});
